import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

const API_BASE = API_ORIGIN;

// Same toggle-switch control ProfilePage.jsx uses for Account
// Status/Access Status -- kept as its own small copy here (no shared
// components folder for it yet) so both pages look identical.
function ToggleSwitch({ checked, disabled, onChange, label, required }) {
  return (
    <div className="d-flex align-items-center gap-2">
      <label className={`profile-toggle${disabled ? " profile-toggle-disabled" : ""}`}>
        <input
          type="checkbox"
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
        />
        <span className="profile-toggle-slider" />
      </label>
      <span className={disabled ? "text-muted" : "fw-semibold text-dark"}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </span>
    </div>
  );
}

export default function EditUserForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Raw record as loaded from the server -- drives the read-only
  // display. `form` below only gets populated once EDIT is clicked.
  const [userData, setUserData] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    UserName: "", Email: "", RoleID: "", MobileNo: "",
    DepartmentID: "", CompanyID: "", IsEnabled: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");

  const loadUser = () => {
    setLoading(true);
    authFetch(`${API_BASE}/users/${id}`)
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          setLoadError(data.error || "Could not load this user.");
          return;
        }
        setLoadError("");
        setUserData(data);
      })
      .catch(() => setLoadError("Network error while loading this user."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    authFetch(`${API_BASE}/roles`).then((res) => res.json()).then((data) => setRoles(Array.isArray(data) ? data : [])).catch(() => setRoles([]));
    authFetch(`${API_BASE}/departments`).then((res) => res.json()).then((data) => setDepartments(Array.isArray(data) ? data : [])).catch(() => setDepartments([]));
    authFetch(`${API_BASE}/companies`).then((res) => res.json()).then((data) => setCompanies(Array.isArray(data) ? data : [])).catch(() => setCompanies([]));
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startEditing = () => {
    if (!userData) return;
    setSaveError("");
    setSavedMsg("");
    setForm({
      UserName: userData.UserName || "",
      Email: userData.Email || "",
      RoleID: userData.RoleID || "",
      MobileNo: userData.MobileNo || "",
      DepartmentID: userData.DepartmentID || "",
      CompanyID: userData.CompanyID || "",
      IsEnabled: !!userData.IsEnabled,
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (submitting) return;
    setIsEditing(false);
    setSaveError("");
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError("");

    if (!form.UserName.trim() || !form.Email.trim() || !form.RoleID) {
      setSaveError("Please fill in Name, Email and Role.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE}/users/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          UserName: form.UserName.trim(),
          Email: form.Email.trim(),
          RoleID: form.RoleID,
          MobileNo: form.MobileNo.trim() || null,
          DepartmentID: form.DepartmentID || null,
          CompanyID: form.CompanyID || null,
          IsEnabled: form.IsEnabled,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setSaveError(data.error || "Failed to update user.");
        return;
      }

      setIsEditing(false);
      setSavedMsg("User updated successfully.");
      loadUser();
      // Per request, saving now sends the admin straight back to the
      // Users list instead of leaving them on this page -- there's
      // nothing more to do here once the update succeeds.
      navigate(ROUTES.ACCOUNT_USERS);
    } catch (err) {
      setSaveError("Network error while updating the user.");
    } finally {
      setSubmitting(false);
    }
  };

  // Restyled to match ProfilePage.jsx's "User Details" card exactly --
  // photo circle, green field labels in a row g-3 / col-md-6 grid,
  // Account Status/Access Status shown as toggle switches, and a
  // single EDIT button that swaps the whole card between a read-only
  // display and an editable form (Cancel/Save Changes), instead of the
  // old always-editable inputs/selects with a permanent "Save Changes"
  // button and no photo. Per request, this page now looks the same as
  // Profile. The one thing intentionally left out is photo *upload*:
  // that endpoint takes a plain numeric user id, but this page's `id`
  // route param is the encrypted id (Account > Users links to it that
  // way) -- would need a small backend change to support editing
  // someone else's photo, so for now the photo just displays.
  if (loading) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center py-5">
          <p className="text-muted mb-0">Loading...</p>
        </div>
      </div>
    );
  }

  if (loadError && !userData) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="alert alert-danger mb-3">{loadError}</div>
          <Link to={ROUTES.ACCOUNT_USERS} className="btn btn-outline-secondary btn-sm">
            &larr; Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const displayName = userData?.UserName || "-";
  const photoUrl = userData?.ProfileImage ? `${API_BASE}/profile_photo/${userData.ProfileImage}` : null;
  const initials = displayName && displayName !== "-" ? displayName[0].toUpperCase() : "?";

  return (
    <>
      <div className="d-flex align-items-center justify-content-between page-title-head">
        <div className="flex-grow-1">
          <h4 className="m-0 page-main-title">Edit User</h4>
        </div>
        <Link to={ROUTES.ACCOUNT_USERS} className="btn btn-outline-secondary btn-sm">
          &larr; Back to Users
        </Link>
      </div>

      <div className="row">
        <div className="col-12 col-md-8 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom px-4 py-3">
              <h5 className="fw-bold text-green fs-5 m-0">User Details</h5>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="card-body p-4">
                {loadError && <div className="alert alert-danger">{loadError}</div>}
                {isEditing && saveError && <div className="alert alert-danger">{saveError}</div>}

                <div className="col-12 mb-4 text-center">
                  <label className="form-label fw-medium text-green d-block mb-3">Profile Photo</label>
                  <div className="d-inline-block position-relative">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={displayName}
                        className="object-fit-cover rounded-circle shadow-sm"
                        height="110"
                        width="110"
                        style={{ border: "4px solid #0e4d52" }}
                      />
                    ) : (
                      <span
                        className="d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm"
                        style={{
                          height: 110,
                          width: 110,
                          border: "4px solid #0e4d52",
                          background: "#123840",
                          color: "#fff",
                          fontSize: "2.5rem",
                          fontWeight: 700
                        }}
                      >
                        {initials}
                      </span>
                    )}
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Name{isEditing && <span className="text-danger"> *</span>}
                    </label>
                    {isEditing ? (
                      <input
                        className="form-control"
                        value={form.UserName}
                        onChange={handleFormChange("UserName")}
                        required
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={displayName} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Mob No.</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        className="form-control"
                        value={form.MobileNo}
                        onChange={handleFormChange("MobileNo")}
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={userData?.MobileNo || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">
                      Email ID{isEditing && <span className="text-danger"> *</span>}
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        className="form-control"
                        value={form.Email}
                        onChange={handleFormChange("Email")}
                        required
                      />
                    ) : (
                      <input className="form-control custom-field py-2" value={userData?.Email || "-"} type="email" readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Role</label>
                    {isEditing ? (
                      <select className="form-select" value={form.RoleID} onChange={handleFormChange("RoleID")}>
                        <option value="">-- Select Role --</option>
                        {roles.map((r) => (
                          <option key={r.RoleID} value={r.RoleID}>{r.RoleName}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={userData?.RoleName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Department</label>
                    {isEditing ? (
                      <select className="form-select" value={form.DepartmentID} onChange={handleFormChange("DepartmentID")}>
                        <option value="">-- Select Department --</option>
                        {departments.map((d) => (
                          <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={userData?.DepartmentName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-green mb-1">Company</label>
                    {isEditing ? (
                      <select className="form-select" value={form.CompanyID} onChange={handleFormChange("CompanyID")}>
                        <option value="">-- Select Company --</option>
                        {companies.map((c) => (
                          <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
                        ))}
                      </select>
                    ) : (
                      <input className="form-control custom-field py-2" value={userData?.CompanyName || "-"} readOnly />
                    )}
                  </div>

                  <div className="col-md-6 d-flex align-items-center">
                    {/* Computed field -- Active once the user sets their
                        own password -- never editable, same as
                        ProfilePage.jsx. */}
                    <ToggleSwitch checked={userData?.Status === "Active"} disabled label="Account Status" />
                  </div>

                  <div className="col-md-6 d-flex align-items-center">
                    {isEditing ? (
                      <ToggleSwitch
                        checked={form.IsEnabled}
                        onChange={(val) => setForm((prev) => ({ ...prev, IsEnabled: val }))}
                        label="Access Status"
                      />
                    ) : (
                      <ToggleSwitch checked={!!userData?.IsEnabled} disabled label="Access Status" />
                    )}
                  </div>
                </div>

                {!isEditing && savedMsg && (
                  <div className="alert alert-success d-flex align-items-center mt-4 mb-0">
                    <i className="ti ti-circle-check me-2 fs-5" />
                    <span>{savedMsg}</span>
                  </div>
                )}

                <div className="text-end mt-4">
                  {isEditing ? (
                    <div className="d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-light border" onClick={cancelEditing} disabled={submitting}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
                        {submitting ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="btn btn-primary px-4" onClick={startEditing}>
                      EDIT
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Same custom-field/toggle-switch styling as ProfilePage.jsx,
          scoped to this page. */}
      <style>{`
        .custom-field {
          background-color: #f3f3f3 !important;
          border: 1px solid #dcdcdc !important;
          color: #333333 !important;
          border-radius: 2px;
        }

        .profile-toggle {
          position: relative;
          display: inline-block;
          width: 38px;
          height: 20px;
          margin: 0;
        }
        .profile-toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .profile-toggle-slider {
          position: absolute;
          inset: 0;
          background-color: #ccc;
          transition: 0.2s;
          border-radius: 20px;
          cursor: pointer;
        }
        .profile-toggle-slider:before {
          content: "";
          position: absolute;
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background-color: #fff;
          transition: 0.2s;
          border-radius: 50%;
        }
        .profile-toggle input:checked + .profile-toggle-slider {
          background-color: #124d52;
        }
        .profile-toggle input:checked + .profile-toggle-slider:before {
          transform: translateX(18px);
        }
        .profile-toggle-disabled .profile-toggle-slider {
          cursor: not-allowed;
          opacity: 0.55;
        }
      `}</style>
    </>
  );
}
